import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import logo from './assets/logofinal-flat.png';
import { useTranslation } from 'react-i18next';
import { useAuth } from './AuthContext';
import NotificationCenter from './NotificationCenter';
import MessagesNavBell from './MessagesNavBell';
import './Navbar.css';
// Ensure Bootstrap dropdown JS is loaded ONCE (avoid double-binding that can instantly close menus)
if (typeof window !== 'undefined') {
  try {
    const hasBs = !!(window.bootstrap && window.bootstrap.Dropdown);
    if (!hasBs && !window.__wv_bootstrap_loading__) {
      window.__wv_bootstrap_loading__ = true;
      import('bootstrap/dist/js/bootstrap.bundle.min.js')
        .then(() => { window.__wv_bootstrap_loaded__ = true; })
        .catch(() => {});
    }
  } catch {}
}

const NAV_LANG_KEY = 'weavevo_lang';
const NAV_LANG_FALLBACK_KEY = 'weavevo_language';

const NAV_I18N = {
  en: {
    user_fallback: 'User',
    search_log: 'Search:',
    search_placeholder: 'Search',
    search_aria: 'Search Weavevo',
    discover_weavevo: 'Discover Weavevo',
    logo_aria: 'Logo',

    home: 'Home',
    my_medias: 'My media',
    explore: 'Explore',
    create: 'Create',

    start_project: 'Start your weave',
    start_project_desc: 'Bring your ideas to life',
    add_media: 'Add a media',
    add_media_desc: 'Share your photos, videos or creations',
    collaborate: 'Contribute to a weave',
    collaborate_desc: 'Bring your ideas to a project.',
    start_story: 'Start a story',

    portfolio: 'My portfolio',
    portfolio_title: 'My portfolio',
    portfolio_creator: 'As a Weave creator',
    portfolio_creator_desc: 'Projects I created (drafts, active, completed)',
    portfolio_weaver: 'As a Weaver',
    portfolio_weaver_desc: 'Projects I contribute to',

    messages: 'Messages',
    notifications: 'Notifications',
    active_users: 'Active users',

    settings: 'Settings',
    legal: 'Legal',
    legal_notice: 'Legal notice',
    privacy_policy: 'Privacy policy',
    terms: 'Terms of use (ToS)',
    cookies: 'Cookie policy',
    accessibility: 'Accessibility & GDPR',

    discover: 'Discover',
    discover_media: 'Discover media',
    about: 'About',
    blog: 'Blog / News',
    faq: 'FAQ / Help',

    support: 'Support',
    contact_support: 'Contact / Support',

    my_account: 'My account',
    logout: 'Log out',
  },
  fr: {
    user_fallback: 'Utilisateur',
    search_log: 'Recherche:',
    search_placeholder: 'Search',
    search_aria: 'Rechercher sur Weavevo',
    discover_weavevo: 'Découvrir Weavevo',
    logo_aria: 'Logo',

    home: 'Accueil',
    my_medias: 'Mes Médias',
    explore: 'Explorer',
    create: 'Créer',

    start_project: 'Lancez votre weave',
    start_project_desc: 'Donnez vie à vos idées',
    add_media: 'Ajouter un média',
    add_media_desc: 'Partagez vos photos, vidéos ou créations',
    collaborate: 'Collaborer à une weave',
    collaborate_desc: 'Apportez vos idées à un projet.',
    start_story: 'Démarrer une histoire',

    portfolio: 'Mon portefeuille',
    portfolio_title: 'Mon portefeuille',
    portfolio_creator: 'En tant que Weave creator',
    portfolio_creator_desc: 'Projets que j\'ai créés (brouillons, actifs, terminés)',
    portfolio_weaver: 'En tant que Weaver',
    portfolio_weaver_desc: 'Projets où j\'interviens comme contributeur',

    messages: 'Messages',
    notifications: 'Notifications',
    active_users: 'Utilisateurs actifs',

    settings: 'Paramètres',
    legal: 'Légal',
    legal_notice: 'Mentions légales',
    privacy_policy: 'Politique de confidentialité',
    terms: 'Conditions d\'utilisation (CGU)',
    cookies: 'Politique de cookies',
    accessibility: 'Accessibilité & RGPD',

    discover: 'Découvrir',
    discover_media: 'Découvrir les médias',
    about: 'À propos',
    blog: 'Blog / Actualités',
    faq: 'FAQ / Aide',

    support: 'Support',
    contact_support: 'Contact / Support',

    my_account: 'Mon Compte',
    logout: 'Déconnexion',
  },
};

function safeReadLang() {
  try {
    const raw =
      localStorage.getItem(NAV_LANG_KEY) ||
      localStorage.getItem(NAV_LANG_FALLBACK_KEY) ||
      '';
    const norm = String(raw || '').toLowerCase().slice(0, 2);
    return norm === 'fr' ? 'fr' : 'en';
  } catch {
    return 'en';
  }
}

function safeEnsureLang(next) {
  try {
    const raw =
      localStorage.getItem(NAV_LANG_KEY) ||
      localStorage.getItem(NAV_LANG_FALLBACK_KEY) ||
      '';
    if (!raw) localStorage.setItem(NAV_LANG_KEY, next);
  } catch { }
}


function safeDecodeJwtPayload(token) {
  try {
    const part = String(token || '').split('.')[1];
    if (!part) return {};
    const base64 = part.replace(/-/g, '+').replace(/_/g, '/');
    const json = decodeURIComponent(
      window.atob(base64).split('').map((c) =>
        '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2)
      ).join('')
    );
    return JSON.parse(json);
  } catch {
    return {};
  }
}

function safeReadStoredKcUser() {
  try {
    const raw = localStorage.getItem('kc_user');
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
}

function isTmpAdminPayload(payload = {}) {
  const email = String(payload.email || '').trim();
  const identifiers = [
    payload.preferred_username,
    payload.username,
    payload.name,
    payload.sub,
    email,
    email.includes('@') ? email.split('@')[0] : '',
  ]
    .map((v) => String(v || '').trim().toLowerCase())
    .filter(Boolean);

  return identifiers.includes('tmpadm');
}

function Navbar({ onLogout = () => { } }) {
  const auth = useAuth();
  const { i18n } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();

  // Topbar global search (Weavevo)
  const readNavQueryFromLocation = (loc) => {
    try {
      const p = new URLSearchParams(loc?.search || '');
      return p.get('query') || p.get('q') || '';
    } catch {
      return '';
    }
  };

  const [navQuery, setNavQuery] = useState(() => readNavQueryFromLocation(location));

  useEffect(() => {
    // Keep the input in sync when user lands on /search or /weavevoSearch (supports ?query or ?q)
    if (location.pathname !== '/search' && location.pathname !== '/weavevoSearch') return;
    setNavQuery(readNavQueryFromLocation(location));
  }, [location.pathname, location.search]);

  const submitNavSearch = (e) => {
    try { e?.preventDefault?.(); } catch { }
    const q = String(navQuery || '').trim();
    // Use "query" param name (your Search page + API use it)
    navigate(q ? `/search?query=${encodeURIComponent(q)}` : '/search');
  };

  // -------------------- NAV DEBUG (console + optional on-screen overlay) --------------------
  const debugEnabled = (() => {
    try {
      const qs = new URLSearchParams(location.search || "");
      return (
        qs.get("debugNav") === "1" ||
        localStorage.getItem("wv_debug_nav") === "1"
      );
    } catch {
      return false;
    }
  })();

  const [debugOpen, setDebugOpen] = useState(false);
  const [debugLines, setDebugLines] = useState([]);
  const debugRef = useRef({ lines: [] });

  const pushNavLog = (label, data) => {
    try {
      const ts = new Date().toISOString().slice(11, 19);
      const line = `[${ts}] ${label}${data ? " " + JSON.stringify(data) : ""}`;

      // Always log to console (you asked for logs)
      // eslint-disable-next-line no-console
      console.log("%c[Navbar]", "color:#0ea5e9;font-weight:700", line);

      // Optional on-screen overlay (only if debugEnabled)
      if (debugEnabled) {
        debugRef.current.lines = [line, ...debugRef.current.lines].slice(0, 25);
        setDebugLines(debugRef.current.lines);
      }
    } catch (e) {
      // eslint-disable-next-line no-console
      console.log("[Navbar] pushNavLog error", e);
    }
  };

  useEffect(() => {
    pushNavLog("mounted", { path: location.pathname, search: location.search });

    const onBsDropdown = (e) => {
      try {
        const t = e?.target;
        const label =
          t?.getAttribute?.("aria-label") ||
          t?.textContent?.trim?.()?.slice(0, 40) ||
          t?.className ||
          "unknown";
        pushNavLog(e.type, { target: label });

        // Extra diagnostic: check if menu stays open after "shown"
        if (e.type === "shown.bs.dropdown") {
          setTimeout(() => {
            try {
              const toggle = e?.target;
              const parent = toggle?.closest?.(".dropdown, .dropend, .dropup, .dropstart") || toggle?.parentElement;
              const menu = parent?.querySelector?.(".dropdown-menu");
              const isOpen = !!(menu && menu.classList.contains("show"));
              pushNavLog("post-shown-check", { target: label, menuOpen: isOpen });
            } catch (err2) {
              pushNavLog("post-shown-check-error", { error: String(err2) });
            }
          }, 0);
        }
      } catch (err) {
        pushNavLog("dropdown-event-error", { err: String(err) });
      }
    };

    const events = ["show.bs.dropdown", "shown.bs.dropdown", "hide.bs.dropdown", "hidden.bs.dropdown"];
    events.forEach((evt) => document.addEventListener(evt, onBsDropdown, true));

    // Detect if Bootstrap registered on window (may be undefined depending on bundler)
    try {
      const hasWindowBootstrap = !!(window.bootstrap && window.bootstrap.Dropdown);
      const flags = {
        hasWindowBootstrap,
        wv_bootstrap_loading: !!window.__wv_bootstrap_loading__,
        wv_bootstrap_loaded: !!window.__wv_bootstrap_loaded__,
      };
      pushNavLog("bootstrap-check", flags);

      // If Bootstrap is missing (e.g. route-first load), dynamically load it once.
      if (!hasWindowBootstrap && !window.__wv_bootstrap_loading__) {
        window.__wv_bootstrap_loading__ = true;
        pushNavLog("bootstrap-load", { mode: "dynamic-import" });
        import('bootstrap/dist/js/bootstrap.bundle.min.js')
          .then(() => {
            window.__wv_bootstrap_loaded__ = true;
            pushNavLog("bootstrap-ready", { hasWindowBootstrap: !!(window.bootstrap && window.bootstrap.Dropdown) });
          })
          .catch((e) => pushNavLog("bootstrap-load-error", { error: String(e) }));
      }
    } catch (e) {
      pushNavLog("bootstrap-check-error", { error: String(e) });
    }

    return () => {
      events.forEach((evt) => document.removeEventListener(evt, onBsDropdown, true));
    };
  }, []);

  useEffect(() => {
    pushNavLog("route", { path: location.pathname, search: location.search });
  }, [location.pathname, location.search]);

  // Language: default EN. Settings page can set localStorage(NAV_LANG_KEY) to "fr" or "en".
  const [lang, setLang] = useState(() => safeReadLang());

  useEffect(() => {
    const read = () => {
      const next = safeReadLang();
      setLang(next);
      safeEnsureLang(next);
    };

    read();
    window.addEventListener('storage', read);
    window.addEventListener('focus', read);

    return () => {
      window.removeEventListener('storage', read);
      window.removeEventListener('focus', read);
    };
  }, []);

  // If language is changed from Settings page, we often navigate after saving;
  // re-read language on navigation to refresh immediately.
  useEffect(() => {
    const next = safeReadLang();
    if (next !== lang) setLang(next);
  }, [location.pathname, location.search, lang]);

  // Keep i18next in sync, but no language switch UI in the navbar.
  useEffect(() => {
    try {
      const current = String(i18n?.language || '').toLowerCase().slice(0, 2);
      if (i18n?.changeLanguage && current !== lang) {
        i18n.changeLanguage(lang).catch(() => { });
      }
    } catch { }
  }, [i18n, lang]);

  const TXT = NAV_I18N?.[lang] || NAV_I18N.en;

  const isDashboardFiltered =
    location.pathname === '/dashboard' && location.search.includes('filter=');

  const [profile, setProfile] = useState(null);
  const [avatarBust, setAvatarBust] = useState(0);

  const token = auth?.token || (() => {
    try {
      return localStorage.getItem('kc_token') || '';
    } catch {
      return '';
    }
  })();

  // Robust admin detection: auth.user is sometimes incomplete after login,
  // so we merge kc_user and the decoded Keycloak access token.
  // This keeps the Active users menu visible for the real tmpadm token.
  const storedUserPayload = safeReadStoredKcUser();
  const decodedTokenPayload = safeDecodeJwtPayload(token);
  const user = auth?.user || {};
  const tokenParsed = { ...user, ...storedUserPayload, ...decodedTokenPayload };

  const groups = tokenParsed?.groups || [];
  const normalizedGroups = groups?.map((g) => g.replace(/^\//, '')) || [];
  const isMaintainer = normalizedGroups.includes('maintainer');
  const isScreenwriter = normalizedGroups.includes('screenwriter');
  const username = tokenParsed?.preferred_username || tokenParsed?.username || tokenParsed?.email || TXT.user_fallback;
  const isTmpAdminUser = isTmpAdminPayload(tokenParsed);

  const handleLogout = () => {
    localStorage.removeItem('kc_token');
    localStorage.removeItem('kc_refreshToken');
    localStorage.removeItem('kc_user');
    window.location.href = '/';
  };

  useEffect(() => {
    if (!token) return;
    fetch('/myProfil', {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((res) => res.json())
      .then((data) => setProfile(data))
      .catch((err) =>
        console.error(lang === 'fr' ? 'Erreur profil' : 'Profile error', err)
      );
  }, [token, lang]);

  useEffect(() => {
    const onProfileUpdated = (e) => {
      const detail = e?.detail || {};
      setProfile((prev) => (prev ? { ...prev, ...detail } : { ...detail }));
      setAvatarBust(Date.now()); // force image refresh
    };
    window.addEventListener('profile-updated', onProfileUpdated);
    return () => window.removeEventListener('profile-updated', onProfileUpdated);
  }, []);

  return (
    <div className="d-flex flex-column">

      {/* Topbar droite : avatar + menu utilisateur */}
      <div
        className="topbar-navbar"
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          height: '70px',
          zIndex: 1030,
          paddingLeft: '80px',
          paddingRight: '20px',
          backgroundColor: 'white',
          border: 'none',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
        }}
      >
        {/* Left: Search (stretches until avatar) */}
        <div
          className="d-flex align-items-center"
          style={{ flex: '1 1 auto', minWidth: 0, gap: 12 }}
        >
          {/* ✅ Search bar expands (Pinterest-like) */}
          <form
            onSubmit={submitNavSearch}
            className="d-none d-md-flex align-items-center wv-topbar-searchgap"
            style={{ width: '100%', flex: '1 1 auto', minWidth: 0 }}
            role="search"
            aria-label={TXT.search_aria}
          >
            <div className="input-group" style={{ width: '100%' }}>
              <span
                className="input-group-text border-0"
                style={{
                  background: '#f6f6f6',
                  borderRadius: '999px 0 0 999px',
                  paddingLeft: 14,
                  paddingRight: 10,
                  height: 46,
                }}
              >
                <i className="bi bi-search"></i>
              </span>

              <input
                value={navQuery}
                onChange={(e) => setNavQuery(e.target.value)}
                placeholder={TXT.search_placeholder}
                className="form-control border-0"
                style={{
                  background: '#f6f6f6',
                  boxShadow: 'none',
                  fontSize: '0.95rem',
                  height: 46,
                }}
              />
              <button
                type="submit"
                className="btn border-0"
                style={{
                  background: '#f6f6f6',
                  borderRadius: '0 999px 999px 0',
                  paddingLeft: 12,
                  paddingRight: 20,
                  height: 46,
                }}
                aria-label={TXT.search_aria}
                title={TXT.search_aria}
              >
              </button>
            </div>
          </form>

          {/* Search icon (mobile) */}
          <button
            type="button"
            onClick={() => navigate('/search')}
            className="btn d-flex d-md-none align-items-center justify-content-center wv-topbar-searchgap"
            style={{
              width: 46,
              height: 46,
              borderRadius: 999,
              background: '#f6f6f6',
              border: 'none',
            }}
            aria-label={TXT.search_aria}
            title={TXT.search_aria}
          >
            <i className="bi bi-search"></i>
          </button>
        </div>

        {/* Right: avatar + menu utilisateur */}
        <div className="d-flex align-items-center justify-content-end" style={{ flex: '0 0 auto' }}>
          <div className="dropdown ms-1">
            <button
              type="button"
              className="btn d-flex align-items-center gap-2 p-0 border-0 bg-transparent"
              data-bs-toggle="dropdown"
              data-bs-display="static"
              data-bs-boundary="viewport"
              aria-expanded="false"
              onClick={() => pushNavLog("click-avatar-menu")}
            >
              <img
                key={avatarBust}
                src={
                  profile?.avatar_url
                    ? `${profile.avatar_url}${avatarBust
                      ? (profile.avatar_url.includes('?') ? '&' : '?') + 'v=' + avatarBust
                      : ''
                    }`
                    : `https://ui-avatars.com/api/?name=${encodeURIComponent(
                      tokenParsed?.name || username || 'User'
                    )}&background=dee2e6&color=495057`
                }
                className="rounded-circle avatar-img"
                style={{ width: 48, height: 48, objectFit: 'cover' }}
                alt="Avatar"
              />
              <i
                className="bi bi-chevron-down"
                style={{
                  fontSize: '0.75rem',
                  marginTop: 2,
                  color: '#6c757d',
                }}
              />
            </button>

            <ul
              className="dropdown-menu dropdown-menu-end shadow rounded-4 p-3"
              style={{ minWidth: 250 }}
            >
              <div className="text-center mb-3">
                <img
                  src={`https://ui-avatars.com/api/?name=${encodeURIComponent(
                    tokenParsed?.name || username || 'User'
                  )}&background=ffffff&color=0d1b2a`}
                  className="rounded-circle mb-2"
                  style={{ width: 56, height: 56 }}
                  alt="User avatar"
                />
                <div className="fw-bold">{tokenParsed?.name || username}</div>
                <div className="text-muted small">
                  {tokenParsed?.email || 'user@email.com'}
                </div>
              </div>

              <li>
                <Link to="/account" className="dropdown-item d-flex align-items-center gap-2">
                  <i className="bi bi-person-gear"></i>
                  <span>{TXT.my_account}</span>
                </Link>
              </li>

              <li>
                <Link to="/myMedias" className="dropdown-item d-flex align-items-center gap-2">
                  <i className="bi bi-collection"></i>
                  <span>{TXT.my_medias}</span>
                </Link>
              </li>

              {isTmpAdminUser ? (
                <li>
                  <Link
                    to="/active-users"
                    className="dropdown-item d-flex align-items-center gap-2 fw-semibold"
                    title={TXT.active_users}
                  >
                    <i className="bi bi-person-check"></i>
                    <span>{TXT.active_users}</span>
                    <span className="badge text-bg-primary ms-auto">tmpadm</span>
                  </Link>
                </li>
              ) : null}

              <li><hr className="dropdown-divider" /></li>

              <li className="text-center mt-2">
                <button
                  className="btn btn-outline-danger btn-sm rounded-pill w-100 d-flex align-items-center justify-content-center gap-2"
                  onClick={handleLogout}
                >
                  <i className="bi bi-box-arrow-right"></i>
                  <span>{TXT.logout}</span>
                </button>
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* Barre verticale gauche */}
      <nav
        className="nav-rail d-flex flex-column align-items-start bg-light p-3 shadow"
        style={{
          width: '80px',
          position: 'fixed',
          top: 0,
          bottom: 'var(--wv-app-footer-nav-stop, 58px)',
          left: 0,
          zIndex: 20000,
          transition: 'bottom 180ms ease',
        }}
      >
        <style>{`
          /* Alignement uniforme de toutes les icônes du rail */
          .nav-rail .icon-btn{
            width:44px;height:44px;
            display:flex;align-items:center;justify-content:center;
            border:none;background:transparent;padding:0;margin:0;
          }
          .nav-rail .icon-btn i{ font-size:1.5rem; line-height:1; }

          /* Dropdown hardening (prevents clipping / behind layers) */
          .nav-rail, .nav-rail .dropend, .nav-rail .dropdown { overflow: visible; }
          .nav-rail .dropdown-menu{ z-index: 20001; }
          .topbar-navbar .dropdown-menu{ z-index: 20002; }

          /* Label textuel pour "Accueil" au survol */
          .nav-rail .nav-item-with-label {
            position: relative;
          }
          .nav-rail .nav-item-with-label .nav-label {
            position: absolute;
            left: 50%;
            top: 48px;
            transform: translateX(-50%) translateY(4px);
            background: #000;
            color: #fff;
            padding: 2px 10px;
            border-radius: 999px;
            font-size: 0.75rem;
            white-space: nowrap;
            opacity: 0;
            pointer-events: none;
            transition: opacity .15s ease-out, transform .15s ease-out;
          }
          .nav-rail .nav-item-with-label:hover .nav-label {
            opacity: 1;
            transform: translateX(-50%) translateY(0);
          }

          /* Explorer panel (Pinterest-like) */
          .explorer-panel{ min-width:320px; border-radius:16px; padding:14px; }

          .explorer-title{ font-weight:700; margin:4px 8px 12px; }
          .explorer-item{ display:flex; gap:12px; align-items:center; padding:10px; border-radius:12px; text-decoration:none; color:inherit; }
          .explorer-item:hover{ background:#f6f6f6; }
          .explorer-icon{
            width:44px;height:44px;border-radius:12px;
            display:flex;align-items:center;justify-content:center;
            background:#ececec;
          }
          .explorer-item .label{ font-weight:600; }
          .explorer-item .desc{ font-size:.875rem;color:#6b7280; margin-top:2px; }
        `}</style>

        {/* Logo */}
        <Link
          to="/"
          className="mb-2 d-flex align-items-center text-decoration-none"
          aria-label={TXT.logo_aria}
        >
          <img src={logo} alt="Weavevo" style={{ height: 60 }} />
        </Link>

        {/* Icônes principales */}
        <ul className="nav flex-column w-100 gap-4">
          {/* Home */}
          <li title={TXT.home}>
            <Link
              className="nav-link text-dark d-flex justify-content-center icon-btn"
              to="/public"
              aria-label={TXT.home}
            >
              <i className="bi bi-house"></i>
            </Link>
          </li>

          {/* Mes Médias */}
          <li title={TXT.my_medias}>
            <Link
              className="nav-link text-dark d-flex justify-content-center icon-btn"
              to="/myMedias"
              aria-label={TXT.my_medias}
            >
              <i className="bi bi-collection-play"></i>
            </Link>
          </li>

          {/* Create / Explore menu restored */}
          <li className="w-100 text-center" title={TXT.explore}>
            <div className="dropend w-100 d-flex justify-content-center">
              <button
                type="button"
                className="icon-btn"
                data-bs-toggle="dropdown"
                data-bs-display="static"
                data-bs-boundary="viewport"
                aria-expanded="false"
                aria-label={TXT.explore}
                onClick={() => pushNavLog("click-explorer-menu")}
              >
                <i className="bi bi-compass"></i>
              </button>

              <div className="dropdown-menu shadow explorer-panel">
                <div className="explorer-title">{TXT.create}</div>

                <Link to="/wizard" className="explorer-item">
                  <div className="explorer-icon">
                    <i className="bi bi-collection"></i>
                  </div>
                  <div>
                    <div className="label">{TXT.start_project}</div>
                    <div className="desc">{TXT.start_project_desc}</div>
                  </div>
                </Link>

                <Link to="/myMedias" state={{ openAddMedia: true }} className="explorer-item">
                  <div className="explorer-icon">
                    <i className="bi bi-patch-plus"></i>
                  </div>
                  <div>
                    <div className="label">{TXT.add_media}</div>
                    <div className="desc">{TXT.add_media_desc}</div>
                  </div>
                </Link>

                <Link to="/explore" className="explorer-item">
                  <div className="explorer-icon">
                    <i className="bi bi-speedometer2"></i>
                  </div>
                  <div>
                    <div className="label">{TXT.collaborate}</div>
                    <div className="desc">{TXT.collaborate_desc}</div>
                  </div>
                </Link>
              </div>
            </div>
          </li>

         

            <li title={TXT.start_story}>
              <Link
                className="nav-link text-dark d-flex justify-content-center icon-btn"
                to="/wizard"
                aria-label={TXT.start_story}
              >
                <i className="bi bi-magic"></i>
              </Link>
            </li>
          {/* My portfolio menu restored */}

          <li className="w-100 text-center" title={TXT.portfolio}>
            <div className="dropend w-100 d-flex justify-content-center">
              <button
                className="icon-btn"
                type="button"
                data-bs-toggle="dropdown"
                data-bs-display="static"
                data-bs-boundary="viewport"
                aria-expanded="false"
                aria-label={TXT.portfolio}
                onClick={() => pushNavLog("click-portfolio-menu")}
              >
                <i className="bi bi-bar-chart"></i>
              </button>

              <div className="dropdown-menu shadow explorer-panel">
                <div className="explorer-title">{TXT.portfolio_title}</div>

                <Link to="/portfolio?mode=master" className="explorer-item">
                  <div className="explorer-icon">
                    <i className="bi bi-person-badge-fill"></i>
                  </div>
                  <div>
                    <div className="label">{TXT.portfolio_creator}</div>
                    <div className="desc">{TXT.portfolio_creator_desc}</div>
                  </div>
                </Link>

                <Link to="/portfolio?mode=contrib" className="explorer-item">
                  <div className="explorer-icon">
                    <i className="bi bi-people-fill"></i>
                  </div>
                  <div>
                    <div className="label">{TXT.portfolio_weaver}</div>
                    <div className="desc">{TXT.portfolio_weaver_desc}</div>
                  </div>
                </Link>
              </div>
            </div>
          </li>


          {/* Messages */}
          <li title={TXT.messages}>
            <div className="d-flex justify-content-center icon-btn" aria-label={TXT.messages}>
              <MessagesNavBell />
            </div>
          </li>

          {/* Admin-only: visible only for tmpadm */}
          {isTmpAdminUser ? (
            <li className="w-100 text-center" title={TXT.active_users}>
              <Link
                className="nav-link d-flex justify-content-center icon-btn"
                to="/active-users"
                aria-label={TXT.active_users}
                style={{
                  background: '#e7f1ff',
                  color: '#0d6efd',
                  borderRadius: 14,
                  boxShadow: '0 8px 18px rgba(13,110,253,.14)',
                }}
              >
                <i className="bi bi-person-check-fill"></i>
              </Link>
            </li>
          ) : null}
        </ul>

        {/* Notifications */}
        <div className="mt-4 d-flex flex-column align-items-center gap-2 w-100 wv-rail-dropup">
          <div
            className="d-flex justify-content-center icon-btn"
            aria-label={TXT.notifications}
            title={TXT.notifications}
          >
            <NotificationCenter />
          </div>
        </div>

        {/* Paramètres */}
        <div
          className="mt-auto d-flex flex-column align-items-center gap-2 pt-2 w-100"
          style={{
            overflow: 'visible',
            paddingBottom: 'calc(70px + env(safe-area-inset-bottom))',
          }}
        >
          <div className="dropdown dropend wv-rail-dropup w-100 text-center" style={{ marginBottom: 8 }}>
            <button
              type="button"
              className="btn p-0 border-0 w-100 d-flex justify-content-center align-items-center"
              style={{ height: 22 }}
              data-bs-toggle="dropdown"
              data-bs-display="static"
              data-bs-boundary="viewport"
              aria-expanded="false"
              title={TXT.settings}
              aria-label={TXT.settings}
              onClick={() => pushNavLog("click-settings-menu")}
            >
              <i className="bi bi-gear fs-4" style={{ lineHeight: 1 }}></i>
            </button>

            <ul
              className="dropdown-menu shadow rounded-4 p-3"
              style={{
                width: 'min(320px, calc(100vw - 110px))',
                maxHeight: '75vh',
                overflowY: 'auto',
              }}
            >
              {/* SETTINGS */}
              <li className="mb-2">
                <Link to="/settings" className="dropdown-item d-flex align-items-center gap-2">
                  <i className="bi bi-sliders2"></i>
                  <span>{TXT.settings}</span>
                </Link>
              </li>

              {isTmpAdminUser ? (
                <li className="mb-2">
                  <Link
                    to="/active-users"
                    className="dropdown-item d-flex align-items-center gap-2 fw-semibold"
                    style={{ color: '#0d6efd' }}
                  >
                    <i className="bi bi-person-check-fill"></i>
                    <span>{TXT.active_users}</span>
                  </Link>
                </li>
              ) : null}

              <li><hr className="dropdown-divider" /></li>

              {/* LEGAL (from FooterRunwayStyle.jsx) */}
              <li className="px-2 pt-1 pb-2">
                <small className="text-muted" style={{ fontWeight: 700, letterSpacing: '.02em' }}>
                  {TXT.legal}
                </small>
              </li>

              <li>
                <Link to="/mentions-legales" className="dropdown-item d-flex align-items-center gap-2">
                  <i className="bi bi-file-earmark-text"></i>
                  <span>{TXT.legal_notice}</span>
                </Link>
              </li>
              <li>
                <Link to="/politique-confidentialite" className="dropdown-item d-flex align-items-center gap-2">
                  <i className="bi bi-fingerprint"></i>
                  <span>{TXT.privacy_policy}</span>
                </Link>
              </li>
              <li>
                <Link to="/cgu" className="dropdown-item d-flex align-items-center gap-2">
                  <i className="bi bi-journal-text"></i>
                  <span>{TXT.terms}</span>
                </Link>
              </li>
              <li>
                <Link to="/politique-cookies" className="dropdown-item d-flex align-items-center gap-2">
                  <i className="bi bi-cookie"></i>
                  <span>{TXT.cookies}</span>
                </Link>
              </li>
              <li className="mb-1">
                <Link to="/accessibilite-rgpd" className="dropdown-item d-flex align-items-center gap-2">
                  <i className="bi bi-universal-access"></i>
                  <span>{TXT.accessibility}</span>
                </Link>
              </li>

              <li><hr className="dropdown-divider" /></li>

              {/* DISCOVER (from FooterRunwayStyle.jsx) */}
              <li className="px-2 pt-1 pb-2">
                <small className="text-muted" style={{ fontWeight: 700, letterSpacing: '.02em' }}>
                  {TXT.discover}
                </small>
              </li>

              <li>
                <Link to="/about" className="dropdown-item d-flex align-items-center gap-2">
                  <i className="bi bi-info-circle"></i>
                  <span>{TXT.about}</span>
                </Link>
              </li>
              <li className="mb-1">
                <Link to="/faq" className="dropdown-item d-flex align-items-center gap-2">
                  <i className="bi bi-question-circle"></i>
                  <span>{TXT.faq}</span>
                </Link>
              </li>

              <li><hr className="dropdown-divider" /></li>

              {/* SUPPORT (from FooterRunwayStyle.jsx) */}
              <li className="px-2 pt-1 pb-2">
                <small className="text-muted" style={{ fontWeight: 700, letterSpacing: '.02em' }}>
                  {TXT.support}
                </small>
              </li>

              <li>
                <Link to="/contact" className="dropdown-item d-flex align-items-center gap-2">
                  <i className="bi bi-envelope"></i>
                  <span>{TXT.contact_support}</span>
                </Link>
              </li>
            </ul>
          </div>
        </div>
      </nav>

      <main style={{ marginLeft: '80px', paddingTop: '360px', paddingRight: '20px' }}>
        {/* Contenu des routes */}
      </main>

      {debugEnabled ? (
        <div
          style={{
            position: "fixed",
            bottom: 10,
            left: 90,
            zIndex: 30000,
            fontFamily: "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, \"Liberation Mono\", \"Courier New\", monospace",
            fontSize: 12,
            background: "rgba(15,23,42,.92)",
            color: "#fff",
            borderRadius: 12,
            padding: debugOpen ? 10 : "8px 10px",
            boxShadow: "0 14px 40px rgba(0,0,0,.25)",
            maxWidth: "min(520px, calc(100vw - 110px))",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10 }}>
            <div style={{ fontWeight: 800, letterSpacing: ".02em" }}>
              Navbar debug
            </div>
            <div style={{ display: "flex", gap: 8 }}>
              <button
                type="button"
                onClick={() => setDebugOpen((v) => !v)}
                style={{
                  border: "1px solid rgba(255,255,255,.18)",
                  background: "transparent",
                  color: "#fff",
                  borderRadius: 999,
                  padding: "2px 10px",
                  fontWeight: 800,
                  cursor: "pointer",
                }}
              >
                {debugOpen ? "Hide" : "Show"}
              </button>
              <button
                type="button"
                onClick={() => {
                  debugRef.current.lines = [];
                  setDebugLines([]);
                  pushNavLog("clear");
                }}
                style={{
                  border: "1px solid rgba(255,255,255,.18)",
                  background: "transparent",
                  color: "#fff",
                  borderRadius: 999,
                  padding: "2px 10px",
                  fontWeight: 800,
                  cursor: "pointer",
                }}
              >
                Clear
              </button>
            </div>
          </div>

          {debugOpen ? (
            <div style={{ marginTop: 8, maxHeight: 220, overflow: "auto", paddingRight: 6 }}>
              {debugLines.length ? (
                debugLines.map((l, i) => (
                  <div key={i} style={{ opacity: 0.95, padding: "2px 0", borderBottom: "1px solid rgba(255,255,255,.08)" }}>
                    {l}
                  </div>
                ))
              ) : (
                <div style={{ opacity: 0.8 }}>No events yet. Click a menu.</div>
              )}
              <div style={{ marginTop: 6, opacity: 0.7 }}>
                Tip: set <b>localStorage.wv_debug_nav</b> = "1" or add <b>?debugNav=1</b>
              </div>
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

export default Navbar;
